import { useState, type ReactNode } from "react";
import type { FocusView } from "@pushback-and-chaos/contracts";
import { FOCUS, SKILL_LABEL } from "../design/copy.js";
import { Pill } from "../ui/primitives.js";
import { FreeTextInput } from "../ui/input.js";

/**
 * THE FOCUS, IN TWO PARTS (coach plan step 9, C08; re-cut for the desk on
 * 2026-09-23): the one thing the learner is working on. What it shows is the
 * server's projection of the focus's events: who suggested it and how,
 * whether the learner chose it, what they tried, what they said about using
 * it elsewhere (their words, labelled as such), and what code found when it
 * looked at a later finished engagement (a verdict word and its note). Every
 * control sends one event the engine checks; nothing here is a numeral; and
 * no line promotes one kind of evidence into another.
 *
 * `FocusBody` is the reading and the controls; `FocusComposer` is the one
 * text box — naming a focus when there is none, or reporting a use elsewhere
 * once one is chosen — kept apart so the expanded focus card can pin it at
 * its foot. `FocusPanel` is the two together.
 */
export type SelfReportContext = "WORK" | "ENGAGEMENT" | "ELSEWHERE";

export interface FocusBodyProps {
  readonly focus: { readonly current: FocusView | null } | null;
  readonly busy: boolean;
  readonly onSuggest: (text?: string) => void;
  readonly onChoose: (focusId: string) => void;
  readonly onAction: (focusId: string, action: "decline" | "done") => void;
}

export interface FocusComposerProps {
  readonly focus: { readonly current: FocusView | null } | null;
  readonly busy: boolean;
  readonly onSuggest: (text?: string) => void;
  readonly onSelfReport: (focusId: string, text: string, context: SelfReportContext) => void;
  readonly onOpenSettings: () => void;
}

export interface FocusPanelProps extends FocusBodyProps, FocusComposerProps {}

/** "calibration", from the focus's skill. */
export function focusSkillWord(focus: FocusView): string {
  return SKILL_LABEL[focus.state.dimension] ?? focus.state.dimension.toLowerCase();
}

export function FocusBody({ focus, busy, onSuggest, onChoose, onAction }: FocusBodyProps): ReactNode {
  if (focus === null) return null;
  const current = focus.current;

  if (current === null) {
    return (
      <div data-testid="focus-panel" className="pc-practice">
        <p className="m-0 mb-3 font-sans text-small">{FOCUS.none}</p>
        <div className="flex flex-wrap gap-3">
          <Pill onClick={() => onSuggest()} disabled={busy}>{FOCUS.suggest}</Pill>
        </div>
      </div>
    );
  }

  const { state, standing, focusId } = current;
  const stands = (Object.keys(FOCUS.standing) as (keyof typeof FOCUS.standing)[]).filter((k) => standing[k]).map((k) => FOCUS.standing[k]);

  return (
    <div data-testid="focus-panel" data-status={state.status} className="pc-practice">
      <p className="pcs-quiet m-0 mb-1">{focusSkillWord(current)} · {FOCUS.statuses[state.status]}</p>
      <p className="m-0 mb-2 font-display text-body font-bold">{state.text}</p>
      <p className="m-0 mb-3 font-sans text-small text-ink-soft">{FOCUS.provenance[state.origin]}</p>

      {state.status === "SUGGESTED" ? (
        <div className="flex flex-wrap gap-3">
          <Pill onClick={() => onChoose(focusId)} disabled={busy}>{FOCUS.choose}</Pill>
          <Pill onClick={() => onAction(focusId, "decline")} disabled={busy}>{FOCUS.decline}</Pill>
        </div>
      ) : null}

      {state.status === "CHOSEN" ? (
        <>
          {stands.length > 0 ? <p data-testid="focus-standing" className="m-0 mb-3 font-sans text-small">{stands.join("; ")}.</p> : null}
          {state.self_reports.map((r) => (
            <p key={r.at} className="pcs-said m-0 mb-2"><span className="font-bold">{FOCUS.saidLabel}</span> {r.text} <span className="text-ink-soft">({FOCUS.reportContexts[r.context]})</span></p>
          ))}
          {state.reviews.length === 0 ? (
            <p className="m-0 mb-3 font-sans text-small text-ink-soft">{FOCUS.noReviewYet}</p>
          ) : (
            <div data-testid="focus-reviews" className="mb-3">
              <p className="pcs-quiet m-0 mb-1">{FOCUS.reviewLabel}</p>
              {state.reviews.map((r) => (
                <p key={r.session_id} data-verdict={r.verdict} className="m-0 mb-1 font-sans text-small"><span className="font-bold">{r.engagement}, {FOCUS.verdicts[r.verdict]}.</span> {r.note}</p>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <Pill onClick={() => onAction(focusId, "done")} disabled={busy}>{FOCUS.done}</Pill>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Naming a focus when there is none; reporting a use elsewhere, with where, once one is chosen. Nothing otherwise. */
export function FocusComposer({ focus, busy, onSuggest, onSelfReport, onOpenSettings }: FocusComposerProps): ReactNode {
  const [draft, setDraft] = useState("");
  const [where, setWhere] = useState<SelfReportContext>("WORK");
  if (focus === null) return null;
  const current = focus.current;
  if (current === null) {
    return (
      <div data-testid="focus-composer">
        <FreeTextInput
          placeholder={FOCUS.namePlaceholder}
          disabled={busy}
          onSubmit={(text) => { setDraft(""); onSuggest(text); }}
          onOpenSettings={onOpenSettings}
          draft={draft}
          onDraftChange={setDraft}
          compact
        />
      </div>
    );
  }
  if (current.state.status !== "CHOSEN") return null;
  return (
    <div data-testid="focus-composer">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <span className="font-sans text-small">{FOCUS.reportWhere}</span>
        {(Object.keys(FOCUS.reportContexts) as SelfReportContext[]).map((c) => (
          <Pill key={c} onClick={() => setWhere(c)} disabled={busy} className={where === c ? "bg-marker" : ""}>{FOCUS.reportContexts[c]}</Pill>
        ))}
      </div>
      <FreeTextInput
        placeholder={FOCUS.reportPlaceholder}
        disabled={busy}
        disabledReason={busy ? FOCUS.working : undefined}
        onSubmit={(text) => { setDraft(""); onSelfReport(current.focusId, text, where); }}
        onOpenSettings={onOpenSettings}
        draft={draft}
        onDraftChange={setDraft}
        compact
      />
    </div>
  );
}

/** Body and composer in one column, for a surface without a foot to pin the box to. */
export function FocusPanel(props: FocusPanelProps): ReactNode {
  if (props.focus === null) return null;
  return (
    <div className="mt-6">
      <p className="pcs-quiet m-0 mb-2">{FOCUS.label}</p>
      <FocusBody focus={props.focus} busy={props.busy} onSuggest={props.onSuggest} onChoose={props.onChoose} onAction={props.onAction} />
      <div className="mt-3">
        <FocusComposer focus={props.focus} busy={props.busy} onSuggest={props.onSuggest} onSelfReport={props.onSelfReport} onOpenSettings={props.onOpenSettings} />
      </div>
    </div>
  );
}
